import { fetchJson } from "./http";

export interface PolymarketMarketMeta {
	question: string;
	startDate: string | null;
	endDate: string | null;
}

// Public, unauthenticated, CORS-open -- safe to call directly from the browser.
const GAMMA_API_BASE = "https://gamma-api.polymarket.com";

export async function fetchPolymarketMarketMeta(
	conditionId: string,
): Promise<PolymarketMarketMeta | null> {
	const url = new URL("/markets", GAMMA_API_BASE);
	url.searchParams.set("condition_ids", conditionId);

	const response = await fetch(url.toString());
	if (!response.ok) return null;

	const body = (await response.json()) as Array<{
		question?: string;
		startDate?: string;
		endDate?: string;
	}>;
	const market = body[0];
	if (!market) return null;

	return {
		question: market.question ?? "",
		startDate: market.startDate ?? null,
		endDate: market.endDate ?? null,
	};
}

// --- Public market data, used directly so these views do not depend on our own API. ---
// Both hosts answer with `Access-Control-Allow-Origin: *` and need no key.

const DATA_API_BASE = "https://data-api.polymarket.com/v2";

export interface GammaMarket {
	conditionId: string;
	question?: string;
	slug?: string;
	outcomes?: string | string[];
	outcomePrices?: string | string[];
	clobTokenIds?: string | string[];
	volume24hr?: number;
	volume1wk?: number;
	volume1mo?: number;
	volume?: string | number;
	volumeNum?: number;
	liquidity?: string | number;
	liquidityNum?: number;
	oneDayPriceChange?: number;
	oneHourPriceChange?: number;
	oneWeekPriceChange?: number;
	startDate?: string;
	createdAt?: string;
	endDate?: string;
	closed?: boolean;
	tags?: { label?: string }[];
	/** Set on sports and esports fixtures. */
	gameStartTime?: string;
	eventStartTime?: string;
	sportsMarketType?: string;
	/** UMA oracle state: "proposed", "disputed" and so on. Absent until someone proposes a result. */
	umaResolutionStatus?: string;
	events?: { id?: string; title?: string; startTime?: string }[];
}

function getPublicJson<T>(url: URL, signal?: AbortSignal): Promise<T> {
	return fetchJson<T>(url.toString(), { signal });
}

const GAMMA_PAGE = 100; // the endpoint returns at most this many, whatever `limit` asks for

function fetchMarketPage(offset: number, endsAfter?: string, signal?: AbortSignal): Promise<GammaMarket[]> {
	const url = new URL("/markets", GAMMA_API_BASE);
	if (endsAfter) url.searchParams.set("end_date_min", endsAfter);
	url.searchParams.set("limit", String(GAMMA_PAGE));
	url.searchParams.set("offset", String(offset));
	url.searchParams.set("closed", "false");
	url.searchParams.set("active", "true");
	url.searchParams.set("order", "volume24hr");
	url.searchParams.set("ascending", "false");
	url.searchParams.set("include_tag", "true");
	return getPublicJson<GammaMarket[]>(url, signal);
}

/** Open markets by 24h volume, with tags. */
export async function fetchActiveMarkets(pages = 2, signal?: AbortSignal): Promise<GammaMarket[]> {
	const results = await Promise.all(
		Array.from({ length: pages }, (_, i) => fetchMarketPage(i * GAMMA_PAGE, undefined, signal)),
	);
	const byId = new Map(results.flat().map((m) => [m.conditionId, m]));
	return [...byId.values()];
}

/**
 * Open markets that end after `endsAfter`, by 24h volume. Same-day games and elections fill
 * the top of the plain list, so without this filter 400 rows stop near $35K of daily volume
 * and miss the quieter long-dated markets a spike is most meaningful on.
 */
export async function fetchLongDatedMarkets(endsAfter: string, pages = 4, signal?: AbortSignal): Promise<GammaMarket[]> {
	const results = await Promise.all(
		Array.from({ length: pages }, (_, i) => fetchMarketPage(i * GAMMA_PAGE, endsAfter, signal)),
	);
	return results.flat();
}

const CONDITION_CHUNK = 20;

function conditionChunk(ids: string[], closed: boolean, signal?: AbortSignal): Promise<GammaMarket[]> {
	const url = new URL("/markets", GAMMA_API_BASE);
	for (const id of ids) url.searchParams.append("condition_ids", id);
	url.searchParams.set("limit", String(ids.length));
	url.searchParams.set("include_tag", "true");
	if (closed) url.searchParams.set("closed", "true");
	return getPublicJson<GammaMarket[]>(url, signal);
}

async function conditionChunks(ids: string[], closed: boolean, signal?: AbortSignal): Promise<GammaMarket[]> {
	const chunks: string[][] = [];
	for (let i = 0; i < ids.length; i += CONDITION_CHUNK) chunks.push(ids.slice(i, i + CONDITION_CHUNK));
	return (await Promise.all(chunks.map((c) => conditionChunk(c, closed, signal)))).flat();
}

/**
 * Markets by condition id, open or closed. Gamma leaves closed markets out unless asked and then
 * leaves open ones out, so only the ids still missing take a second request.
 */
export async function fetchMarketsByCondition(ids: string[], signal?: AbortSignal): Promise<GammaMarket[]> {
	if (ids.length === 0) return [];
	const open = await conditionChunks(ids, false, signal);
	const found = new Set(open.map((m) => m.conditionId));
	const missing = ids.filter((id) => !found.has(id));
	return missing.length ? [...open, ...(await conditionChunks(missing, true, signal))] : open;
}

/** Server-side market search, flattened from the events it returns. */
export async function searchMarkets(query: string, signal?: AbortSignal): Promise<GammaMarket[]> {
	const url = new URL("/public-search", GAMMA_API_BASE);
	url.searchParams.set("q", query);
	url.searchParams.set("limit_per_type", "10");
	url.searchParams.set("events_status", "active");
	const body = await getPublicJson<{ events?: { markets?: GammaMarket[] }[] }>(url, signal);
	return (body.events ?? []).flatMap((e) => e.markets ?? []).filter((m) => m.conditionId && !m.closed);
}

export interface ProfileHit {
	proxyWallet?: string;
	name?: string | null;
	pseudonym?: string | null;
}

export interface SearchHits {
	markets: GammaMarket[];
	profiles: ProfileHit[];
}

/** Markets and wallet profiles in one call, for the command palette. */
export async function searchEverything(query: string, signal?: AbortSignal): Promise<SearchHits> {
	const url = new URL("/public-search", GAMMA_API_BASE);
	url.searchParams.set("q", query);
	url.searchParams.set("limit_per_type", "8");
	url.searchParams.set("events_status", "active");
	url.searchParams.set("search_profiles", "true");
	// No retries: by the time one would fire, the user has typed something else.
	const body = await fetchJson<{ events?: { markets?: GammaMarket[] }[]; profiles?: ProfileHit[] }>(url.toString(), { signal, retries: 0 });
	return {
		markets: (body.events ?? []).flatMap((e) => e.markets ?? []).filter((m) => m.conditionId && !m.closed),
		profiles: (body.profiles ?? []).filter((p) => p.proxyWallet),
	};
}

export interface LargeTrade {
	proxy_wallet: string;
	side: "BUY" | "SELL";
	token_id: string;
	condition_id: string;
	size: number;
	price: number;
	timestamp: number;
	title: string;
	slug: string | null;
	outcome: string;
	name: string | null;
	pseudonym: string | null;
	transaction_hash: string;
}

export interface LargeTradeQuery {
	minUsd: number;
	limit?: number;
	condition?: string;
	user?: string;
}

export async function fetchLargeTrades(q: LargeTradeQuery, signal?: AbortSignal): Promise<LargeTrade[]> {
	const url = new URL(`${DATA_API_BASE}/trades`);
	url.searchParams.set("limit", String(q.limit ?? 500));
	url.searchParams.set("filterType", "CASH");
	url.searchParams.set("filterAmount", String(q.minUsd));
	// A wallet's own trades count on either side; the market-wide tape would list each fill twice.
	if (!q.user) url.searchParams.set("takerOnly", "true");
	if (q.condition) url.searchParams.set("condition", q.condition);
	if (q.user) url.searchParams.set("user", q.user);
	const body = await getPublicJson<{ data?: LargeTrade[] }>(url, signal);
	return body.data ?? [];
}

export interface HolderRow {
	proxy_wallet: string;
	amount: number;
	name: string | null;
	pseudonym: string | null;
	outcome_index: number;
}

export interface OutcomeHolders {
	token_id: string;
	holders: HolderRow[];
}

/** Largest holders per outcome. The endpoint caps `limit`, so this is never the full list. */
export async function fetchHolders(condition: string, limit = 100, signal?: AbortSignal): Promise<OutcomeHolders[]> {
	const url = new URL(`${DATA_API_BASE}/holders`);
	url.searchParams.set("condition", condition);
	url.searchParams.set("limit", String(limit));
	const body = await getPublicJson<{ data?: OutcomeHolders[] }>(url, signal);
	return body.data ?? [];
}

export interface WalletPosition {
	condition_id: string;
	title: string;
	outcome: string;
	avg_price: number;
	current_price: number;
	current_value: number;
	total_cost_usdc: number;
	realized_pnl: number;
	unrealized_pnl: number;
	redeemable: boolean;
}

export interface WalletPositions {
	positions: WalletPosition[];
	more: boolean;
}

/** Open positions, largest current value first. */
export async function fetchPositions(user: string, limit = 25, signal?: AbortSignal): Promise<WalletPositions> {
	const url = new URL(`${DATA_API_BASE}/positions`);
	url.searchParams.set("user", user);
	url.searchParams.set("limit", String(limit));
	const body = await getPublicJson<{ data?: WalletPosition[]; pagination?: { has_more?: boolean } }>(url, signal);
	return { positions: body.data ?? [], more: Boolean(body.pagination?.has_more) };
}

/** Current value of a wallet's open positions, or null when the API gives none. */
export async function fetchPortfolioValue(user: string, signal?: AbortSignal): Promise<number | null> {
	const url = new URL(`${DATA_API_BASE}/value`);
	url.searchParams.set("user", user);
	const body = await getPublicJson<{ data?: { value?: number } }>(url, signal);
	return typeof body.data?.value === "number" ? body.data.value : null;
}

export interface PublicProfile {
	createdAt?: string;
	name?: string | null;
	pseudonym?: string | null;
	xUsername?: string | null;
}

/** Null for an address with no Polymarket profile, which is an answer rather than a failure. */
export async function fetchPublicProfile(address: string, signal?: AbortSignal): Promise<PublicProfile | null> {
	const url = new URL("/public-profile", GAMMA_API_BASE);
	url.searchParams.set("address", address);
	try {
		return await getPublicJson<PublicProfile>(url, signal);
	} catch (e) {
		if ((e as { status?: number }).status === 404) return null;
		throw e;
	}
}
